import { defineStore, storeToRefs } from 'pinia';
import { getAuth } from 'firebase/auth';
import { getDomain, getQueryString } from '@/utils/util';
import { useCookies } from 'vue3-cookies';
import {
  postAuthLogin,
  postAuthLogout,
  postAuthCodeLogin,
} from '@service/auth';
import useUserStore from '@store/user';
import useSystemStore from '@store/system';

const { cookies } = useCookies();
const useAuthStore = defineStore('auth', {
  // 定义状态：一个函数，返回一个对象
  state: () =>
    ({
      uid: '',
      uuid: '',
      email: '',
      accessToken: '',
      redirectUrl: '',
      isLoggedIn: false,
      isLoading: false,
      isShowCookiePolicy: false,
      isShowPayPopMask: false,
    } as AuthInfo),

  actions: {
    async authState() {
      const code = getQueryString('code');
      const { isEmailVerified } = storeToRefs(useSystemStore());
      const jwt_token = cookies.get('jwt_token');
      const userInfoStorage = {} as AuthInfo;
      const userInfoStorageStr = localStorage.getItem('userInfo') as string;

      if (userInfoStorageStr && userInfoStorageStr !== 'undefined') {
        Object.assign(userInfoStorage, JSON.parse(userInfoStorageStr));
      }

      // 不允许 爬虫 或 作弊用户 登录
      if (window?.navigator?.webdriver) {
        this.logout();
      } else if (jwt_token && userInfoStorage) {
        this.email = userInfoStorage.email;
        this.isLoggedIn = true;
      } else if (code) {
        setTimeout(() => {
          this.login();
        }, 1000);
      } else {
        try {
          this.googleSignIn();
        } catch (e) {
          console.log(e);
        }

        const { currentUser } = await getAuth();

        if (
          currentUser?.emailVerified &&
          currentUser?.providerId !== 'password'
        ) {
          await getAuth().signOut();
        }

        getAuth().onAuthStateChanged(async (user) => {
          if (user?.emailVerified && user?.email) {
            await this.login();
            this.isLoggedIn = true;
            isEmailVerified.value = false;
          } else {
            this.isLoggedIn = false;
          }
        });
      }
    },
    async login(user?: any, sso_type?: string) {
      // providerData
      let status = -1;
      let invite_code = getQueryString('invite_code');
      let invite_channel = getQueryString('ch');
      const code = getQueryString('code');
      const inviteCodeAndChannel = localStorage.getItem(
        'invite_code_and_channel'
      );

      if (!user) {
        const { currentUser } = await getAuth();
        user = currentUser;
      }

      if (user?.providerData) {
        localStorage.setItem('providerData', JSON.stringify(user.providerData));
      } else {
        localStorage.removeItem('providerData');
      }

      if (user?.accessToken) {
        cookies.set('x_sso_token', user.accessToken, 60 * 60, '/', getDomain());
      }

      if (!invite_code || inviteCodeAndChannel?.length) {
        const [inviteCode, channel] = inviteCodeAndChannel?.split('__') || [];
        invite_code = inviteCode;
        invite_channel = channel;
      }

      // 登录成功会在cookie中注入 jwt_token
      if (code) {
        status = await postAuthCodeLogin({
          code,
          type: 'gzh',
          sys_platform: 'vgen',
        });
      } else {
        status = await postAuthLogin({
          sso_type: sso_type || 'firebase',
          sys_platform: 'vgen',
          invite_code,
          invite_channel,
        });
      }

      if (status === 33000) {
        this.temporaryEmailSignOut();
        return;
      } else if (status === 33001) {
        await getAuth().signOut();
        return;
      }

      window?.dataLayer?.push({
        user_id: cookies.get('uuid'),
      });

      this.$patch({
        email: user?.email,
        accessToken: user?.accessToken,
        isLoggedIn: true,
      });

      localStorage.setItem(
        'userInfo',
        JSON.stringify({
          email: this.email,
        })
      );

      localStorage.removeItem('invite_code_and_channel');

      // 预设重定向 url, cb优先级更高
      if (cookies.get('jwt_token')) {
        if (this.redirectUrl) {
          location.href = this.redirectUrl;
        } else {
          window?.top?.location.reload();
        }
      }
    },
    async logout(url = location.pathname as string) {
      const userStore = useUserStore();
      const { uid } = userStore;
      try {
        await getAuth().signOut();
        await postAuthLogout({
          uid,
        });
      } catch (e) {
        console.log(e);
      }

      // 一旦退出，需删除accessToken
      cookies.remove('x_sso_token');
      cookies.remove('jwt_token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('userNewVoice');

      this.$reset();

      if (url) {
        location.href = url;
      }
    },
    async temporaryEmailSignOut() {
      await getAuth().signOut();
      localStorage.removeItem('providerData');
      setTimeout(() => {
        this.$reset();
      }, 0);
    },
    // import PopLogin from '@com/popLogin.vue';
    // 使用该方法，建议在主页中引入
    skipPageOrShowLoginPop(url?: string, cb?: FunctionStringCallback) {
      // 并未登录的，用户拒绝cookie policy 后会触发此功能
      // if (useStorage.get('isAccesptCookie') === false) {
      //   this.isShowCookiePolicy = true;
      //   return;
      // }

      if (this.isLoggedIn) {
        location.href = url || this.redirectUrl || '/avatar';
      } else if (url) {
        this.redirectUrl = url;
      } else {
        location.reload();
      }
    },

    googleSignIn() {
      // @ts-ignore
      window?.google?.accounts?.id?.initialize({
        client_id:
          '555169182660-42fu3mhtlgf6g8g5hgvbpjina2gqmubk.apps.googleusercontent.com',
        callback: this.onSignIn3,
      });

      // @ts-ignore
      window?.google?.accounts?.id?.prompt((notification) => {
        // 如果無法彈出登入視窗 紀錄錯誤訊息
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log(notification);
        }
      });
    },

    onSignIn3(response: any) {
      const credential = response.credential;
      const profile = JSON.parse(
        decodeURIComponent(
          escape(
            window.atob(
              credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
            )
          )
        )
      );

      if (credential) {
        this.login(
          {
            accessToken: credential,
            ...profile,
          },
          'google'
        );
      }
    },
    async getAuthError(code: string): Promise<string> {
      let msg = '';

      switch (code) {
        case 'auth/email-already-in-use':
        case 'auth/email-already-exists':
          msg = 'auth.emailAlreadyExists';
          break;
        case 'auth/id-token-expired':
        case 'auth/invalid-email-verified':
        case 'auth/invalid-email':
        case 'auth/invalid-login-credentials':
          msg = 'auth.invalidLoginCredentials';
          break;
        case 'auth/wrong-password':
        case 'auth/invalid-password':
        case 'auth/invalid-password-hash':
        case 'auth/invalid-password-salt':
          msg = 'msg.error.wrongPassword';
          break;
        case 'auth/too-many-requests':
          msg = 'auth.tooManyRequests';
          break;
        case 'auth/network-request-failed':
          msg = 'auth.networkError';
          break;
        default:
          msg = 'auth.invalidOperation';
          break;
      }

      return msg;
    },
  },
});

export default useAuthStore;
